import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';
import { useFinanceStore } from '../../store/useFinanceStore';
import { ChevronLeft, ChevronRight } from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface LineChartProps {
  title: string;
  subtitle?: string;
}

const LineChart: React.FC<LineChartProps> = ({ title, subtitle }) => {
  const { theme } = useTheme();
  const { transactions } = useFinanceStore();
  const isDark = theme === 'dark';
  
  // Estado para período selecionado (null = mês atual)
  const [selectedPeriod, setSelectedPeriod] = useState<{ month: number; year: number } | null>(null);

  const currentDate = new Date();
  const displayMonth = selectedPeriod?.month ?? currentDate.getMonth();
  const displayYear = selectedPeriod?.year ?? currentDate.getFullYear();
  const isCurrentMonth = !selectedPeriod;

  // Navegar entre meses
  const navigatePeriod = (direction: 'prev' | 'next') => {
    const current = selectedPeriod || { month: currentDate.getMonth(), year: currentDate.getFullYear() };
    let newMonth = current.month;
    let newYear = current.year;

    if (direction === 'prev') {
      newMonth--;
      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      }
    } else {
      newMonth++;
      if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }
    }

    setSelectedPeriod({ month: newMonth, year: newYear });
  };

  const resetToCurrentMonth = () => {
    setSelectedPeriod(null);
  };

  // Calcula evolução do saldo com dados reais
  const chartData = useMemo(() => {
    const activeTransactions = transactions
      .filter((t) => t.status !== 'cancelled')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const totalDaysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
    const monthKey = `${displayYear}-${String(displayMonth + 1).padStart(2, '0')}`;
    const monthStartKey = `${monthKey}-01`;
    const monthEndKey = `${monthKey}-${String(totalDaysInMonth).padStart(2, '0')}`;

    let runningBalance = 0;

    activeTransactions.forEach((transaction) => {
      if (transaction.date < monthStartKey) {
        runningBalance += transaction.amount;
      }
    });

    const transactionsByDate = activeTransactions.reduce<Record<string, number>>((acc, transaction) => {
      if (transaction.date >= monthStartKey && transaction.date <= monthEndKey) {
        acc[transaction.date] = (acc[transaction.date] ?? 0) + transaction.amount;
      }
      return acc;
    }, {});

    const labels: string[] = [];
    const balances: number[] = [];
    const dates: string[] = [];

    for (let day = 1; day <= totalDaysInMonth; day += 1) {
      const dayKey = `${monthKey}-${String(day).padStart(2, '0')}`;

      if (transactionsByDate[dayKey]) {
        runningBalance += transactionsByDate[dayKey];
      }

      labels.push(`${String(day).padStart(2, '0')}/${String(displayMonth + 1).padStart(2, '0')}`);
      balances.push(runningBalance);
      dates.push(dayKey);
    }

    if (!labels.length) {
      return {
        labels: ['Sem dados'],
        balances: [0],
        dates: [''],
      };
    }

    return { labels, balances, dates };
  }, [transactions, displayMonth, displayYear]);

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Saldo',
        data: chartData.balances,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#10b981',
        pointBorderColor: '#0f172a',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: '#10b981',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3,
      },
    ],
  };

  const tooltipBackground = isDark ? '#0f172a' : '#ffffff';
  const tooltipTitleColor = isDark ? '#f1f5f9' : '#0f172a';
  const tooltipBodyColor = isDark ? '#10b981' : '#047857';
  const tooltipBorderColor = isDark ? '#10b981' : '#5eead4';
  const tickColor = isDark ? '#64748b' : '#0f172a';
  const gridColor = isDark ? '#1e293b' : '#d1fae5';

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: tooltipBackground,
        titleColor: tooltipTitleColor,
        bodyColor: tooltipBodyColor,
        borderColor: tooltipBorderColor,
        borderWidth: 2,
        padding: 12,
        displayColors: false,
        titleFont: {
          size: 13,
          weight: 600,
        },
        bodyFont: {
          size: 16,
          weight: 'bold' as const,
        },
        callbacks: {
          title: function(context: any) {
            const index = context[0].dataIndex;
            const date = new Date(chartData.dates[index]);
            return date.toLocaleDateString('pt-BR', { 
              day: '2-digit', 
              month: 'long',
              year: 'numeric'
            });
          },
          label: function(context: any) {
            const value = context.parsed.y;
            return `Saldo: R$ ${value.toLocaleString('pt-BR', { 
              minimumFractionDigits: 2,
              maximumFractionDigits: 2 
            })}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: gridColor,
          drawBorder: false,
        },
        ticks: {
          color: tickColor,
          font: {
            size: 11,
          },
        },
      },
      y: {
        grid: {
          color: gridColor,
          drawBorder: false,
        },
        ticks: {
          color: tickColor,
          font: {
            size: 11,
          },
          callback: function(value: any) {
            return 'R$ ' + value.toLocaleString('pt-BR');
          },
        },
      },
    },
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  return (
    <div
      className={`rounded-xl p-6 h-64 shadow-2xl transition-colors ${
        isDark
          ? 'bg-slate-900/95 backdrop-blur-sm border border-slate-800'
          : 'bg-white border border-teal-200 shadow-lg shadow-teal-100/30'
      }`}
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {title}
          </h3>
          {subtitle && (
            <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {isCurrentMonth ? subtitle : `${monthNames[displayMonth]} ${displayYear}`}
            </p>
          )}
        </div>
        
        {/* Navegação de período */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigatePeriod('prev')}
            className={`p-1.5 rounded-lg transition-colors ${
              isDark
                ? 'bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white'
                : 'bg-white border border-teal-200 text-teal-600 hover:bg-teal-50'
            }`}
            title="Mês anterior"
          >
            <ChevronLeft size={18} />
          </button>
          
          {!isCurrentMonth && (
            <button
              onClick={resetToCurrentMonth}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                isDark
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-500 hover:to-emerald-600 text-white shadow-md shadow-emerald-200/40'
              }`}
            >
              Hoje
            </button>
          )}
          
          <button
            onClick={() => navigatePeriod('next')}
            className={`p-1.5 rounded-lg transition-colors ${
              isDark
                ? 'bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white'
                : 'bg-white border border-teal-200 text-teal-600 hover:bg-teal-50'
            }`}
            title="Próximo mês"
            disabled={isCurrentMonth}
          >
            <ChevronRight size={18} className={isCurrentMonth ? 'opacity-30' : ''} />
          </button>
        </div>
      </div>
      
      <div className="h-44">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

export default LineChart;